import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Collapse,
  IconButton,
  Paper,
  TablePagination,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { getAuthData } from "../../utils/auth";

const apiUrl = process.env.REACT_APP_API_URL;

const ClienteList = () => {
  const [clientes, setClientes] = useState({ items: [], total: 0 });
  const [expandedCliente, setExpandedCliente] = useState(null);
  const [expandedContrato, setExpandedContrato] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const authData = getAuthData();
  const token = authData?.token;
  const vendedor_id = authData?.vendedorId;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  useEffect(() => {
    const obtenerClientes = async () => {
      try {
        if (!vendedor_id) {
          toast.error("No se pudo obtener el ID del vendedor.");
          return;
        }

        const config = {
          headers: { Authorization: `Bearer ${token}` },
        };

        const response = await axios.get(
          `${apiUrl}/api/v1/clientes/lista/${vendedor_id}?page=${page + 1
          }&per_page=${rowsPerPage}`,
          config
        );
        setClientes(response.data);
      } catch (error) {
        toast.error("Error al obtener la lista de clientes.");
        console.error(error);
      }
    };

    obtenerClientes();
  }, [page, rowsPerPage]);

  const handleToggleCliente = (clienteId) => {
    setExpandedCliente(expandedCliente === clienteId ? null : clienteId);
    setExpandedContrato(null); // Cerrar los detalles de contratos al cambiar de cliente
  };

  const handleToggleContrato = (contratoId) => {
    setExpandedContrato(expandedContrato === contratoId ? null : contratoId);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getEstadoPagoTexto = (estadoPago) => {
    if (estadoPago === "pagado" || estadoPago === "inicial_pagada") {
      return estadoPago === "inicial_pagada" ? "INICIAL PAGADA" : "PAGADO";
    }
    return "PENDIENTE";
  };

  const getEstadoTexto = (estado) => {
    if (estado === "contrato") {
      return "CONTRATO";
    }
    return "SOLICITUD";
  };

  return (
    <Box
      sx={{
        margin: "0 auto",
        padding: 3,
        boxShadow: 3,
        backgroundColor: "#ffffff",
        borderRadius: 2,
      }}
    >
      <Typography
        variant="h6"
        align="center"
        gutterBottom
        sx={{ fontFamily: "Bakbak One, sans-serif", color: "#0A4575" }}
      >
        SOLICITUDES Y CONTRATOS DE CLIENTES
      </Typography>

      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#0A4575" }}>
            <TableRow>
              <TableCell sx={{ color: "#fff" }}>Nombre</TableCell>
              <TableCell sx={{ color: "#fff" }}>Cédula</TableCell>
              <TableCell sx={{ color: "#fff" }}>Teléfono</TableCell>
              <TableCell sx={{ color: "#fff" }}>Dirección</TableCell>
              <TableCell sx={{ color: "#fff" }}>Correo electrónico</TableCell>
              <TableCell sx={{ color: "#fff" }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clientes.items.length > 0 ? (
              clientes.items.map((cliente) => (
                <React.Fragment key={cliente.id}>
                  <TableRow>
                    <TableCell>{cliente.nombre}</TableCell>
                    <TableCell>{cliente.cedula}</TableCell>
                    <TableCell>{cliente.celular}</TableCell>
                    <TableCell>{cliente.direccion}</TableCell>
                    <TableCell>{cliente.email}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleToggleCliente(cliente.id)}
                      >
                        {expandedCliente === cliente.id ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ padding: 2 }}>
                      <Collapse
                        in={expandedCliente === cliente.id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontFamily: "Bakbak One, sans-serif",
                              color: "#0A4575",
                            }}
                          >
                            SOLICITUDES DE CONTRATO
                          </Typography>
                          <TableContainer component={Paper}>
                            <Table size="small" sx={{ width: "100%" }}>
                              <TableHead sx={{ backgroundColor: "#0A4575" }}>
                                <TableRow>
                                  <TableCell sx={{ color: "#fff" }}>
                                    No. Transacción
                                  </TableCell>
                                  <TableCell sx={{ color: "#fff" }}>
                                    Tipo de Servicio
                                  </TableCell>
                                  <TableCell sx={{ color: "#fff" }}>
                                    Fecha Solicitud
                                  </TableCell>
                                  <TableCell sx={{ color: "#fff" }}>
                                    Estado del Pago
                                  </TableCell>
                                  <TableCell sx={{ color: "#fff" }}>
                                    Estado
                                  </TableCell>
                                  <TableCell sx={{ color: "#fff" }}></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {cliente.contratos.length > 0 ? (
                                  cliente.contratos.map((contrato) => (
                                    <React.Fragment key={contrato.id}>
                                      <TableRow>
                                        <TableCell>
                                          {contrato.numero_contrato ||
                                            contrato.numero_solicitud}
                                        </TableCell>
                                        <TableCell>
                                          {contrato.tipo_servicio}
                                        </TableCell>
                                        <TableCell>
                                          {formatDate(contrato.fecha_creacion)}
                                        </TableCell>
                                        <TableCell>
                                          <Button
                                            variant="contained"
                                            sx={{
                                              backgroundColor:
                                                contrato.estadoPago ===
                                                  "pagado" ||
                                                  contrato.estadoPago ===
                                                  "inicial_pagada"
                                                  ? "#4CAF50"
                                                  : "#F44336",
                                              color: "#FFFFFF",
                                              "&:hover": {
                                                backgroundColor:
                                                  contrato.estadoPago ===
                                                    "pagado" ||
                                                    contrato.estadoPago ===
                                                    "inicial_pagada"
                                                    ? "#45A049"
                                                    : "#D32F2F",
                                              },
                                              pointerEvents: "none",
                                            }}
                                          >
                                            {getEstadoPagoTexto(
                                              contrato.estadoPago
                                            )}
                                          </Button>
                                        </TableCell>
                                        <TableCell>
                                          <Button
                                            variant="contained"
                                            sx={{
                                              backgroundColor:
                                                contrato.estado === "contrato"
                                                  ? "#4CAF50"
                                                  : "#F44336",
                                              color: "#FFFFFF",
                                              "&:hover": {
                                                backgroundColor:
                                                  contrato.estado === "contrato"
                                                    ? "#45A049"
                                                    : "#D32F2F",
                                              },
                                              pointerEvents: "none",
                                            }}
                                          >
                                            {getEstadoTexto(contrato.estado)}
                                          </Button>
                                        </TableCell>
                                        <TableCell>
                                          <IconButton
                                            onClick={() =>
                                              handleToggleContrato(contrato.id)
                                            }
                                          >
                                            {expandedContrato ===
                                              contrato.id ? (
                                              <ExpandLessIcon />
                                            ) : (
                                              <ExpandMoreIcon />
                                            )}
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell
                                          colSpan={8}
                                          sx={{ padding: 0, width: "100%" }}
                                        >
                                          <Collapse
                                            in={
                                              expandedContrato === contrato.id
                                            }
                                            timeout="auto"
                                            unmountOnExit
                                          >
                                            <Box
                                              sx={{
                                                margin: 2,
                                                backgroundColor: "#fafafa",
                                              }}
                                            >
                                              <Typography
                                                variant="h6"
                                                gutterBottom
                                                sx={{
                                                  fontFamily:
                                                    "Bakbak One, sans-serif",
                                                  color: "#0A4575",
                                                }}
                                              >
                                                DETALLES DE LAS SOLICITUDES
                                              </Typography>
                                              <TableContainer
                                                component={Paper}
                                                sx={{
                                                  boxShadow: 3,
                                                  width: "100%",
                                                  margin: 0,
                                                  padding: 0,
                                                }}
                                              >
                                                <Table size="small">
                                                  <TableHead
                                                    sx={{
                                                      backgroundColor:
                                                        "#0A4575",
                                                    }}
                                                  >
                                                    <TableRow>
                                                      <TableCell
                                                        sx={{ color: "#fff" }}
                                                      >
                                                        Descripción
                                                      </TableCell>
                                                      <TableCell
                                                        sx={{ color: "#fff" }}
                                                      >
                                                        Modalidad de Pago
                                                      </TableCell>
                                                      <TableCell
                                                        sx={{ color: "#fff" }}
                                                      >
                                                        Pago Inicial
                                                      </TableCell>
                                                      <TableCell
                                                        sx={{ color: "#fff" }}
                                                      >
                                                        Cuotas
                                                      </TableCell>
                                                      <TableCell
                                                        sx={{ color: "#fff" }}
                                                      >
                                                        Monto Cuotas
                                                      </TableCell>
                                                      <TableCell
                                                        sx={{ color: "#fff" }}
                                                      >
                                                        Precio Total
                                                      </TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    {contrato.detalles.map(
                                                      (detalle) => (
                                                        <TableRow
                                                          key={detalle.id}
                                                        >
                                                          <TableCell
                                                            sx={{
                                                              maxWidth: 250,
                                                              overflow:
                                                                "hidden",
                                                              textOverflow:
                                                                "ellipsis",
                                                              whiteSpace:
                                                                "nowrap",
                                                            }}
                                                          >
                                                            {
                                                              detalle.descripcion
                                                            }
                                                          </TableCell>
                                                          <TableCell>
                                                            {
                                                              detalle.modalidadPago
                                                            }
                                                          </TableCell>
                                                          <TableCell>
                                                            {
                                                              detalle.pagoInicial
                                                            }
                                                          </TableCell>
                                                          <TableCell>
                                                            {detalle.cuotas}
                                                          </TableCell>
                                                          <TableCell>
                                                            {
                                                              detalle.montoPorCuota
                                                            }
                                                          </TableCell>
                                                          <TableCell>
                                                            $
                                                            {
                                                              detalle.precioTotal
                                                            }
                                                          </TableCell>
                                                        </TableRow>
                                                      )
                                                    )}
                                                  </TableBody>
                                                </Table>
                                              </TableContainer>
                                            </Box>
                                          </Collapse>
                                        </TableCell>
                                      </TableRow>
                                    </React.Fragment>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={7} align="center">
                                      Este cliente no posee solicitudes
                                      asociadas.
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Usted no posee clientes registrados.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={clientes.total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
        />
      </TableContainer>

      <Button
        variant="contained"
        component={Link}
        to="/ventas"
        sx={{
          marginTop: "20px",
          backgroundColor: "#0A4575",
          color: "#FFFFFF",
          "&:hover": {
            backgroundColor: "#083a5e",
          },
        }}
      >
        Volver
      </Button>
    </Box>
  );
};

export default ClienteList;
